import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { CreateSmallDocumentWithMetadataRequest } from '../../Model/requests/CreateSmallDocumentWithMetadataRequest';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { v4 as uuidv4 } from 'uuid';
import { useSearchContext } from '../search/SearchContext';
import { searchKeys } from '../search/queryKeyFactory';
import { useLoaderContext } from '../loader/GearLoaderContext';
import { LOADER_KEY } from '../../Model/enums/GearLoaderKey';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { FormRetryData } from '../form/DocumentFormContext';
import { useGetCurrentUser } from '../currentUser/useGetCurrentUser';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useCreateSmallDocumentWithMetadata(): UseMutationResult<
  string,
  unknown,
  { dto: CreateSmallDocumentWithMetadataRequest; retry?: boolean }
> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { addCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showLoader } = useLoaderContext();
  const { data: currentUser } = useGetCurrentUser();

  const createSmallDocumentWithMetadata = async (data: {
    dto: CreateSmallDocumentWithMetadataRequest;
    retry?: boolean;
  }): Promise<string> => {
    if (data.retry) {
      throw new Error('Intentional retry failure for testing');
    }

    if (servicesCtx?.documentsService && data.dto) {
      showLoader(true, LOADER_KEY.CREATE_DOCUMENT_METADATA);
      return servicesCtx.documentsService?.createSmallDocumentWithMetadata(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.createSmallDocumentWithMetadata,
    mutationFn: createSmallDocumentWithMetadata,
    onSuccess: async (data, variables) => {
      showLoader(false, LOADER_KEY.CREATE_DOCUMENT_METADATA);

      showToast({
        type: GEAR_NOTIFICATION_TYPE.SUCCESS,
        message: translate('custom.toolbar.actions.form.docCreatedSuccessfully')
      });

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.createDocument.createDocumentSuccessTitle', {
          fileName: variables.dto.file.name
        }),
        message: translate('custom.notifications.notificationCenter.createDocument.createDocumentSuccessMessage'),
        timestamp: new Date(),
        type: 'success'
      });

      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }
    },
    onError: (data, variables) => {
      showLoader(false, LOADER_KEY.CREATE_DOCUMENT_METADATA);

      const retryIndex = currentUser?.indexes.find(index => index.id === variables.dto.indexId);

      if (retryIndex) {
        const retryData: FormRetryData = {
          formType: 'new',
          metadata: variables.dto.metadata,
          contentType: variables.dto.ctName,
          retryIndex,
          fileUploadRequired: true
        };

        addCenterNotification({
          id: uuidv4(),
          title: translate('custom.notifications.notificationCenter.createDocument.createDocumentFailedTitle'),
          message: translate('custom.notifications.notificationCenter.createDocument.createDocumentFailedMessage', {
            fileName: variables.dto.file.name
          }),
          timestamp: new Date(),
          type: 'error',
          formRetryData: retryData
        });
      }
    }
  });
}
